import { CustomButton } from "../../components/CustomButton";
import { useDispatch, useSelector } from "react-redux";
import styles from "./style.module.css";
import {
  API_FB_CREATE_APP,
  API_FB_APP_STATUS,
} from "../../api/account.service";
import { useRef, useState } from "react";
import { ReactComponent as FacebookIcon } from "../../svg/Facebook.svg";
import { ReactComponent as DummyVideoIcon } from "../../svg/dummy-video.svg";
import { Divider } from "primereact/divider";
import { useParams } from "react-router-dom";
import { fbStatus } from "./fb-constants";
import { subscription } from "../../reducers/authSlice";
import { useLayout } from "../../context/LayoutContext";
import CopyInput from "./CopyInput";
import { Toast } from "primereact/toast";

function FbRegistrationModule() {
  const [appStatus, setStatus] = useState(fbStatus.PENDING);
  const { user } = useSelector((state) => state.authenticate);
  const [createAppLoading, setAppLoading] = useState(false);
  const [generatedURLLoading, setGeneratedURLLoading] = useState(false);
  const { toastMessage } = useLayout();
  const [generatedURL, setGeneratedURL] = useState("");
  const toast = useRef(null);

  const dispatch = useDispatch();
  const { companyId } = useParams();

  const createAppCall = () => {
    setAppLoading(true);
    API_FB_CREATE_APP(companyId)
      .then((data) => {
        setAppLoading(false);
        window.open(data.link, "_blank");
      })
      .catch(() => {
        setAppLoading(false);
        toastMessage({ severity: "error", detail: "Unable to open Facebook onboarding." });
      });
  };

  const handleGenerateURL = () => {
    setGeneratedURLLoading(true);
    API_FB_CREATE_APP(companyId)
      .then((data) => {
        setGeneratedURLLoading(false);
        setGeneratedURL(data.link);
        toast.current.show({
          severity: "success",
          summary: "URL generated",
          detail: data?.message,
          life: 3000,
        });
      })
      .catch(() => {
        setGeneratedURLLoading(false);
        toastMessage({ severity: "error", detail: "Unable to generate onboarding URL." });
      });
  };

  const fetchStatus = async () => {
    try {
      const data = await API_FB_APP_STATUS(companyId);

      if (!data) {
        toastMessage({ severity: "error", detail: "Failed to fetch app status." });
        return;
      }

      if (data.status !== fbStatus.PENDING && data?.companyId) {
        await dispatch(subscription(user?.companyId)).unwrap();
      } else {
        setStatus(data?.status || fbStatus.PENDING);
      }

      toast.current.show({
        severity: "success",
        summary: "Status checked",
        detail: data?.message || "Status refreshed successfully.",
        life: 3000,
      });
    } catch (error) {
      console.error("Error in fetchStatus:", error);
      toastMessage({
        severity: "error",
        detail: error?.message || "An unexpected error occurred while checking app status.",
      });
    }
  };

  return (
    <div className={styles.module}>
      <Toast ref={toast} />

      <div className="surface-panel">
        <div className="surface-panel-header">
          <div>
            <h2 className="surface-panel-title">Facebook onboarding</h2>
          </div>
          <span className="status-pill warning">{appStatus}</span>
        </div>

        <div className={styles.stepsGrid}>
          <div className={styles.stepCard}>
            <span className={styles.stepNumber}>01</span>
            <h3>Prepare a fresh phone number</h3>
            <p>
              Use a number that is not already attached to WhatsApp to avoid blocking the
              registration flow.
            </p>
          </div>

          <div className={styles.stepCard}>
            <span className={styles.stepNumber}>02</span>
            <h3>Launch Facebook registration</h3>
            <p>Open the onboarding flow directly, or generate a sharable URL for later.</p>
            <div className={styles.actionRow}>
              <button
                className={styles.fbPrimaryButton}
                onClick={createAppCall}
                type="button"
              >
                {createAppLoading ? (
                  <i className="pi pi-spinner pi-spin" />
                ) : (
                  <>
                    <FacebookIcon />
                    <span>Continue with Facebook</span>
                  </>
                )}
              </button>

              {!generatedURL ? (
                <button
                  className={styles.fbSecondaryButton}
                  onClick={handleGenerateURL}
                  type="button"
                >
                  {generatedURLLoading ? (
                    <i className="pi pi-spinner pi-spin" />
                  ) : (
                    <span>Generate URL</span>
                  )}
                </button>
              ) : (
                <CopyInput value={generatedURL} />
              )}
            </div>
          </div>

          <div className={styles.stepCard}>
            <span className={styles.stepNumber}>03</span>
            <h3>Confirm completion</h3>
            <p>
              After Facebook setup finishes, run a status check so the platform can sync
              the company state.
            </p>
            <CustomButton label="Check Status" onClick={fetchStatus} />
          </div>
        </div>

        <Divider />

        <div className={styles.supportRow}>
          <div className={styles.videoCard}>
            <DummyVideoIcon />
            <div>
              <strong>Need a walkthrough?</strong>
              <p>Use the guided video for a step-by-step visual onboarding reference.</p>
            </div>
            <a
              href="https://www.youtube.com/watch?v=8kICTq323DE"
              target="_blank"
              rel="noreferrer"
              className={styles.inlineLink}
            >
              Watch Video
            </a>
          </div>

          <div className={styles.salesHelp}>
            <span className={styles.salesLabel}>Sales Contact</span>
            <strong>Priyanka Ganguly</strong>
            <p>Deskala Sales Manager</p>
            <a
              href="https://wa.me/919900064161"
              target="_blank"
              rel="noreferrer"
              className={styles.inlineLink}
            >
              +91 9900064161
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FbRegistrationModule;
